import { Suspense } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { BuildingOffice2Icon, ClockIcon, Cog6ToothIcon, DocumentTextIcon, GlobeAltIcon, CircleStackIcon, InboxStackIcon, MagnifyingGlassIcon, NewspaperIcon, PaintBrushIcon, ShareIcon, Squares2X2Icon, UsersIcon } from '@heroicons/react/20/solid';
import { Avatar } from '../ui/avatar';
import { Dropdown, DropdownButton } from '../ui/dropdown';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../ui/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '../ui/sidebar';
import { SidebarLayout } from '../ui/sidebar-layout';
import { AccountFooterItem, AccountMenu, BRAND_AVATAR, useAccountIdentity } from '../components/account';
import { OrgSwitcher, SiteSwitcher } from '../components/switchers';
import { DirectoryProvider, useDirectory } from '../lib/directory';
import { canSeeSearch, isStaff, useSession } from '../lib/session';
import { OpenRecsProvider, useOpenRecs } from '../seo/open-recs';
import { Badge } from '../ui/badge';

/**
 * The frame every signed-in page sits in: who you are, where you are, sign out.
 *
 * The organisation's pages are always in the sidebar. Inside a site, the
 * site's own pages follow them under the site's name and domain, and the
 * Manage section at the foot is the site's; at the organisation it is the
 * organisation's. Staff's fleet-wide pages are in the account
 * menu (components/account.tsx), so they don't take a section of their own
 * on every page.
 *
 * Each context has two parts: the pages worked in day to day at the top,
 * and Manage — set-up, settings and records — at the foot.
 */
function ShellFrame() {
  const { client, key } = useParams();
  const { pathname, search: locSearch } = useLocation();
  const { who, initials, signOut: out } = useAccountIdentity();
  const { me, config } = useSession();
  const search = canSeeSearch(me, config);
  const brandAvatar = BRAND_AVATAR;
  const sitePath = client && key ? `/c/${client}/s/${key}` : null;
  // The site's own name and domain head its section; the key until the
  // directory has loaded (or for a site it does not list).
  const { sites } = useDirectory();
  const site = key ? sites?.find((x) => x.key === key) ?? null : null;
  const here = key ? `${client} / ${key}` : (client ?? 'headless');

  return (
    <SidebarLayout
        navbar={
          <Navbar>
            <span className="min-w-0 truncate text-sm/6 font-semibold text-fg">{here}</span>
            <NavbarSpacer />
            <NavbarSection>
              <Dropdown>
                <DropdownButton as={NavbarItem} aria-label="Account">
                  <Avatar square initials={initials} className={brandAvatar} />
                </DropdownButton>
                <AccountMenu anchor="bottom end" who={who} onSignOut={out} />
              </Dropdown>
            </NavbarSection>
          </Navbar>
        }
        sidebar={
          <Sidebar>
            {/* Which organisation, then which of its sites — the two
                switchers stand in for the lists the sidebar used to
                carry, so moving sideways doesn't mean going back. */}
            <SidebarHeader>
              <SidebarSection>
                <OrgSwitcher />
              </SidebarSection>
              {client && (
                <SidebarSection>
                  <SiteSwitcher />
                </SidebarSection>
              )}
            </SidebarHeader>

            <SidebarBody>
              {/* The organisation's pages stay in view inside a site too:
                  its Sites, Newsroom and Content network are a click away
                  wherever you are. */}
              {client && (
                <SidebarSection>
                  <SidebarHeading>Organisation</SidebarHeading>
                  <SidebarItem href={`/c/${client}`} current={pathname === `/c/${client}`}>
                    <Squares2X2Icon />
                    <SidebarLabel>Sites</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`/c/${client}/newsroom`} current={pathname === `/c/${client}/newsroom`}>
                    <NewspaperIcon />
                    <SidebarLabel>Newsroom</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem
                    href={`/c/${client}/content-network`}
                    current={pathname.startsWith(`/c/${client}/content-network`)}
                  >
                    <ShareIcon />
                    <SidebarLabel>Content network</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              )}

              {sitePath && (
                <SidebarSection role="group" aria-labelledby="sidebar-site-heading">
                  <SidebarHeading id="sidebar-site-heading">
                    {/* The site's name and domain, not a section label: in
                        sentence case and mono (the design system's sidebar). */}
                    <span className="block truncate text-sm/5 font-semibold tracking-normal text-fg normal-case">{site?.name ?? key}</span>
                    {site?.domain && <span className="block truncate font-mono text-xs/4 font-normal tracking-normal text-fg-3 normal-case">{site.domain}</span>}
                  </SidebarHeading>
                  <SidebarItem href={sitePath} current={pathname === sitePath}>
                    <GlobeAltIcon />
                    <SidebarLabel>Overview</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`${sitePath}/posts`} current={pathname === `${sitePath}/posts`}>
                    <DocumentTextIcon />
                    <SidebarLabel>Posts</SidebarLabel>
                  </SidebarItem>
                  {search && <SeoGroup sitePath={sitePath} pathname={pathname} query={locSearch} />}
                </SidebarSection>
              )}

              <SidebarSpacer />

              {/* Manage: what is set up rather than worked in day to day —
                  the look, the settings, the record of changes and the
                  store underneath — kept apart at the foot of the list. */}
              {client && !key && (
                <SidebarSection>
                  <SidebarHeading>Manage</SidebarHeading>
                  <SidebarItem href={`/c/${client}/members`} current={pathname === `/c/${client}/members`}>
                    <UsersIcon />
                    <SidebarLabel>Members</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`/c/${client}/activity`} current={pathname === `/c/${client}/activity`}>
                    <ClockIcon />
                    <SidebarLabel>Activity</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`/c/${client}/settings`} current={pathname === `/c/${client}/settings`}>
                    <BuildingOffice2Icon />
                    <SidebarLabel>Settings</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              )}

              {sitePath && (
                <SidebarSection>
                  <SidebarHeading>Manage</SidebarHeading>
                  <SidebarItem href={`${sitePath}/customise`} current={pathname === `${sitePath}/customise`}>
                    <PaintBrushIcon />
                    <SidebarLabel>Customise</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`${sitePath}/settings`} current={pathname === `${sitePath}/settings` || pathname.startsWith(`${sitePath}/settings/`)}>
                    <Cog6ToothIcon />
                    <SidebarLabel>Settings</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`${sitePath}/history`} current={pathname === `${sitePath}/history`}>
                    <ClockIcon />
                    <SidebarLabel>History</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href={`${sitePath}/data`} current={pathname === `${sitePath}/data`}>
                    <CircleStackIcon />
                    <SidebarLabel>Data</SidebarLabel>
                  </SidebarItem>
                  {/* Staff only: the readers routes are on no membership role (ACTIONS K1). */}
                  {isStaff(me) && (
                    <SidebarItem href={`${sitePath}/actions`} current={pathname === `${sitePath}/actions`}>
                      <InboxStackIcon />
                      <SidebarLabel>Reader actions</SidebarLabel>
                    </SidebarItem>
                  )}
                </SidebarSection>
              )}
            </SidebarBody>

            <SidebarFooter className="max-lg:hidden">
              <AccountFooterItem />
            </SidebarFooter>
          </Sidebar>
        }
      >
      {/* The page loads with its route (App.tsx). On a first load the
          frame shows at once and the page area stays empty until the
          page's code is in; between pages React keeps the last page up
          until then (a transition), and the code is usually preloaded. */}
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </SidebarLayout>
  );
}

/**
 * SEO, the one group in the sidebar with pages of its own
 * (seo/SeoLayout.tsx). Closed, it is a single row that opens the section;
 * inside the section it lists the pages, with the open recommendations
 * counted beside theirs. It opens because you are there, not because it
 * was clicked open, so it never needs remembering.
 */
function SeoGroup({ sitePath, pathname, query }: { sitePath: string; pathname: string; query: string }) {
  const seo = `${sitePath}/seo`;
  const inside = pathname === seo || pathname.startsWith(`${seo}/`);
  // The date range carries from page to page, as the pages' own links do.
  const range = inside ? new URLSearchParams(query).get('range') : null;
  const q = range ? `?range=${encodeURIComponent(range)}` : '';
  const { open } = useOpenRecs();
  return (
    <>
      <SidebarItem href={seo}>
        <MagnifyingGlassIcon />
        <SidebarLabel>SEO</SidebarLabel>
        {inside ? <ChevronDownIcon /> : <ChevronRightIcon />}
      </SidebarItem>
      {inside ? (
        <div className="ml-4.5 flex flex-col gap-0.5 border-l border-line pl-2">
          <SidebarItem href={`${seo}${q}`} current={pathname === seo}><SidebarLabel>Performance</SidebarLabel></SidebarItem>
          <SidebarItem href={`${seo}/recommendations${q}`} current={pathname === `${seo}/recommendations`}>
            <SidebarLabel>Recommendations</SidebarLabel>
            {open ? <Badge color="neutral" className="ml-auto">{open}</Badge> : null}
          </SidebarItem>
          <SidebarItem href={`${seo}/opportunities${q}`} current={pathname === `${seo}/opportunities`}><SidebarLabel>Opportunities</SidebarLabel></SidebarItem>
          <SidebarItem href={`${seo}/queries${q}`} current={pathname === `${seo}/queries`}><SidebarLabel>Queries &amp; pages</SidebarLabel></SidebarItem>
          <SidebarItem href={`${seo}/setup${q}`} current={pathname === `${seo}/setup`}><SidebarLabel>Setup</SidebarLabel></SidebarItem>
        </div>
      ) : null}
    </>
  );
}

/** The directory sits above the frame: the switchers and the pages under
 *  them read the same organisation and site lists. The open-recommendations
 *  count is shared between the SEO pages and the sidebar's SEO group. */
export default function Shell() {
  return (
    <DirectoryProvider>
      <OpenRecsProvider>
        <ShellFrame />
      </OpenRecsProvider>
    </DirectoryProvider>
  );
}
